import { useEffect, useMemo, useRef, useState } from 'react'
import { Betrag } from './Betrag'
import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import { Bereichsgrenze, useBereichsgrenze } from './Bereichsgrenze'
import { Dialogfenster } from './Dialogfenster'
import { api, imHintergrund } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { monatFehlt, useMonatsfrage } from './Wirksamkeit'
import { meldeErfolg } from './Erfolgsstelle'
import { useDarf } from './Rechte'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import { Baumzeile, berichtsstand } from './Gliederungsbaum'
import { ohneStatistikkonten, ohneTechnische } from './Festkonten'
import { beschriftung, bewegungsartname, bewertungsebenenname, katalogbezeichnung, kontobezeichnung, ohneVortrag, t, anzahlwort } from './sprache'
import type {
  Aufbaubild,
  Beschriftung,
  Gliederungsknoten,
  Gruppenangabe,
  Importvorschau,
  Katalogeintrag,
  Kernbewegungsart,
  Positionsplan,
  Vorratswert,
  Zielfeld,
} from './typen'

/**
 * Die Zuordnung im Lauf (5.1 Abschnitt 3.11 und 3.15, R589–R592, R602–R606).
 *
 * **Der Bereichswechsel war die Lücke.** Bis zum 2026-09-18 trug die
 * Importvorschau einen Knopf, wenn ein Vorschlag da war — und nichts, wenn
 * keiner da war. Keinen Vorschlag hat aber gerade das **neue** Konto: Die drei
 * Quellen aus R138 können über ein Konto, das im Konzernkontenrahmen noch gar
 * nicht vorkommt, nichts sagen.
 *
 * **Hier steht deshalb der volle Vorrat** (R589): bestehendes Konzernkonto
 * wählen, Vorschlag annehmen, neues Konzernkonto anlegen — derselbe, den der
 * Zuordnungstisch hat, nur ohne dass Gesellschaft, Bewertungsebene, Periode
 * und Datei ein zweites Mal gesagt werden müssen.
 *
 * **Und seit 3.15 auch dasselbe Bild** (R602): links die Lieferung, rechts der
 * Abschluss als Baum. Das Fenster hatte vom Zuordnungstisch den Vorrat an
 * Handgriffen übernommen und das Bild nicht — es fragte über zwei Klapplisten
 * nach einem Namen, statt die Stelle im Abschluss zu zeigen, an die ein Konto
 * gehört.
 */

/** Ersatz, wenn eine Auswahlliste im Hintergrund nicht kam (R1212): keine Wahl. */
const KEINE_KERNE: Kernbewegungsart[] = []
const KEINE_ZIELFELDER: Zielfeld[] = []

/** Ein offenes lokales Konto der Lieferung — je Nummer einmal (R590). */
export type OffenesKonto = {
  lokalnummer: string
  bezeichnung: string | null
  vorschlag: string | null
  vorschlagbezeichnung: string | null
  vorschlagquelle: string | null
  /** Wie viele Zeilen der Lieferung daran hängen — die Zuordnung räumt alle ab. */
  zeilen: number
  /**
   * Die Summe seiner Zeilen in dieser Lieferung (5.1 Abschnitt 3.57, R1214) —
   * `null`, wenn keine einen lesbaren Betrag trägt.
   */
  summe: number | null
}

/**
 * Die offenen Konten einer Vorschau, je lokaler Nummer einmal.
 *
 * **Eine SuSa führt dasselbe Konto mehrfach**, wenn sie nach Bewegungsart
 * aufteilt; zuzuordnen ist es trotzdem nur einmal. Die Zahl der Zeilen bleibt
 * an der Zusammenfassung stehen — sie sagt, wie viel ein Handgriff abräumt.
 */
export function offeneKonten(vorschau: Importvorschau): OffenesKonto[] {
  const einmalig = new Map<string, OffenesKonto>()

  for (const z of vorschau.zeilen) {
    if (z.konto !== null || z.lokalnummer.length === 0) {
      continue
    }

    const schon = einmalig.get(z.lokalnummer)
    if (schon) {
      schon.zeilen += 1
      if (z.betrag !== null) {
        schon.summe = (schon.summe ?? 0) + z.betrag
      }
      continue
    }

    einmalig.set(z.lokalnummer, {
      lokalnummer: z.lokalnummer,
      bezeichnung: z.bezeichnung,
      vorschlag: z.vorschlag,
      vorschlagbezeichnung: z.vorschlagbezeichnung,
      vorschlagquelle: z.vorschlagquelle,
      zeilen: 1,
      summe: z.betrag,
    })
  }

  return [...einmalig.values()]
}

/**
 * Ein gelieferter Bewegungsartcode, der auf keine Bewegungsart zeigt (R593).
 *
 * **Bis zum 2026-09-18 gab es dafür keinen Handgriff** — die Zeile war ein
 * Befund, und geholfen hat nur, die Datei zu ändern.
 */
export type OffeneBewegungsart = {
  code: string
  bezeichnung: string | null
  vorschlag: string | null
  vorschlagbezeichnung: string | null
  vorschlagquelle: string | null
  zeilen: number
}

/**
 * Die offenen Bewegungsartcodes einer Vorschau, je Code einmal.
 *
 * **Groß und klein sind derselbe Code** — wie im Rechenkern (R527): Eine
 * Lieferung, die `Z01` und `z01` mischt, ist ein Fall und nicht zwei.
 */
export function offeneBewegungsarten(vorschau: Importvorschau): OffeneBewegungsart[] {
  const einmalig = new Map<string, OffeneBewegungsart>()

  for (const z of vorschau.zeilen) {
    if (z.bewegungsart.length > 0 || !z.bewegungsartcode) {
      continue
    }

    const schluessel = z.bewegungsartcode.toLowerCase()
    const schon = einmalig.get(schluessel)
    if (schon) {
      schon.zeilen += 1
      continue
    }

    einmalig.set(schluessel, {
      code: z.bewegungsartcode,
      bezeichnung: null,
      vorschlag: z.bewegungsartvorschlag,
      vorschlagbezeichnung: z.bewegungsartvorschlagbezeichnung,
      vorschlagquelle: z.bewegungsartvorschlagquelle,
      zeilen: 1,
    })
  }

  return [...einmalig.values()]
}

/**
 * Die offenen Werte einer Vorschau, **nach Feld gruppiert** (R597).
 *
 * **Je Feld und Code einmal**, wie überall: `V` in dreißig Zeilen ist ein
 * Eintrag, und ein Handgriff räumt alle ab. Gruppen ohne offene Werte gibt es
 * nicht — eine Lieferung mit zwei offenen Segmenten zeigt einen Abschnitt und
 * nicht elf leere.
 */
export function offeneWerte(vorschau: Importvorschau): Map<string, OffeneBewegungsart[]> {
  const nachFeld = new Map<string, Map<string, OffeneBewegungsart>>()

  for (const z of vorschau.zeilen) {
    for (const w of z.offeneWerte ?? []) {
      const feld = nachFeld.get(w.feld) ?? new Map<string, OffeneBewegungsart>()
      nachFeld.set(w.feld, feld)

      const schluessel = w.code.toLowerCase()
      const schon = feld.get(schluessel)
      if (schon) {
        schon.zeilen += 1
        continue
      }

      feld.set(schluessel, {
        code: w.code,
        bezeichnung: null,
        vorschlag: w.vorschlag,
        vorschlagbezeichnung: w.vorschlagbezeichnung,
        vorschlagquelle: w.vorschlagquelle,
        zeilen: 1,
      })
    }
  }

  return new Map([...nachFeld].map(([feld, werte]) => [feld, [...werte.values()]]))
}

/**
 * In welchen Vorräten ein Wert im Lauf entstehen darf (R599).
 *
 * **Der Funktionsbereich fehlt mit Absicht:** Er trägt Bedeutung für die
 * Gewinn- und Verlustrechnung nach dem Umsatzkostenverfahren. Was Bedeutung
 * trägt, entsteht in der Pflege.
 */
export function vorratWaechstMit(feld: string): boolean {
  return feld === 'ic_partner' || feld === 'segment' || /^dim[1-8]$/.test(feld)
}

/** Ein Konto, wie es unter seiner Zielposition steht. */
export type Zielkonto = Gliederungsknoten['konten'][number]

/** Zwischen diesen beiden wird gewechselt (R603) — und nicht zwischen vier. */
export type Bericht = 'bilanz' | 'guv'

/**
 * In welchen Bericht eine Wurzelart gehört.
 *
 * **Vier Arten, zwei Berichte:** Aktiva und Passiva sind die Bilanz, Ertrag
 * und Aufwand die Gewinn- und Verlustrechnung. Vier Knöpfe wären eine
 * Einteilung, die kein Abschluss so nennt.
 */
export function berichtVon(art: string): Bericht {
  const a = art.toLowerCase()
  return a.startsWith('ertrag') || a.startsWith('aufwand') || a.startsWith('guv') ? 'guv' : 'bilanz'
}

/** Eine sichtbare Zeile des Zielbaums (R603). */
export type Zielzeile = {
  knoten: Gliederungsknoten
  /** Ob unter ihr etwas hängt — Unterpositionen oder Konten. */
  hatKinder: boolean
  offen: boolean
  /** Die Konten, die unter dieser Zeile zu zeigen sind — zu sind es keine. */
  konten: Zielkonto[]
}

/**
 * Welche Zeilen des Zielbaums zu sehen sind (R603).
 *
 * **Das Bild kommt flach und mit Ebene** — wer unter wem hängt, sagt `ebene`.
 * Sichtbar ist eine Zeile, wenn **jede** ihrer Oberpositionen aufgeklappt ist;
 * die Konten einer Position erscheinen mit ihr und nicht vorher. Ein
 * Positionsplan hat rund hundert Positionen: vollständig ausgerollt ist er der
 * Grund für die Unübersichtlichkeit und nicht ihr Gegenmittel.
 *
 * **Beim Suchen gilt das nicht.** Wer sucht, hat schon gesagt, was er meint —
 * dann stehen die Treffer da, mit ihren Oberpositionen darüber, damit man
 * sieht, wo sie hängen. Gesucht wird über Position **und** Konto zugleich: Wer
 * `1800` eingibt, meint meistens ein Konto, wer `A.II` eingibt, eine Position.
 */
export function zielzeilen(
  knoten: Gliederungsknoten[],
  bericht: Bericht,
  aufgeklappt: ReadonlySet<string>,
  suche: string,
): Zielzeile[] {
  // Einem technischen Konto wird kein lokales zugeordnet (R1427).
  const sicht = knoten
    .filter((k) => berichtVon(k.art) === bericht)
    // Ohne Statistikkonten: Eine Lieferung darf keines tragen (R1732, R1734).
    .map((k) => ({ ...k, konten: ohneStatistikkonten(ohneTechnische(k.konten, (kt) => kt.schluessel), (kt) => kt.schluessel) }))

  // Die Oberpositionen jeder Zeile — aus der Einrückung, die das Bild mitbringt.
  const pfad: number[] = []
  const ahnen: number[][] = []
  sicht.forEach((k, i) => {
    while (pfad.length > 0) {
      const darueber = sicht[pfad[pfad.length - 1] ?? 0]
      if (darueber && darueber.ebene < k.ebene) {
        break
      }
      pfad.pop()
    }
    ahnen[i] = [...pfad]
    pfad.push(i)
  })

  const hatUnter = (i: number) => {
    const naechste = sicht[i + 1]
    const diese = sicht[i]
    return naechste !== undefined && diese !== undefined && naechste.ebene > diese.ebene
  }
  const gesucht = suche.trim().toLowerCase()

  if (gesucht.length > 0) {
    const zeigen = new Set<number>()
    const eigene = new Map<number, Zielkonto[]>()

    sicht.forEach((k, i) => {
      const trifft =
        k.code.toLowerCase().includes(gesucht) ||
        beschriftung(k.namen).toLowerCase().includes(gesucht)

      const konten = trifft
        ? k.konten
        : k.konten.filter(
            (kt) =>
              kt.nummer.toLowerCase().includes(gesucht) ||
              beschriftung(kt.namen).toLowerCase().includes(gesucht),
          )

      if (!trifft && konten.length === 0) {
        return
      }

      zeigen.add(i)
      ;(ahnen[i] ?? []).forEach((a) => zeigen.add(a))
      eigene.set(i, konten)
    })

    return [...zeigen]
      .sort((a, b) => a - b)
      .flatMap((i) => {
        const knoten = sicht[i]
        return knoten
          ? [
              {
                knoten,
                hatKinder: hatUnter(i) || knoten.konten.length > 0,
                offen: true,
                konten: eigene.get(i) ?? [],
              },
            ]
          : []
      })
  }

  const zeilen: Zielzeile[] = []
  sicht.forEach((k, i) => {
    if (!(ahnen[i] ?? []).every((a) => aufgeklappt.has(sicht[a]?.schluessel ?? ''))) {
      return
    }

    const offen = aufgeklappt.has(k.schluessel)
    zeilen.push({
      knoten: k,
      hatKinder: hatUnter(i) || k.konten.length > 0,
      offen,
      konten: offen ? k.konten : [],
    })
  })

  return zeilen
}

/**
 * Die Berichtsebene, in deren Kontenrahmen ein neues Konto entsteht (R612).
 *
 * **Bis zum 2026-09-18 kam sie aus der Kontextzeile** (R589) — und das war ein
 * Notbehelf, den der Bau selbst gefunden hatte: Gebucht wird in die
 * **Rohdatenschicht**, die die Quelle sagt (R537), und die trägt keinen
 * Positionsplan. Der hängt an der Gruppe darüber.
 *
 * **Das steht aber im Aufbau der Bewertungsebenen** (2.4, R398) und muss
 * nirgends eingestellt werden: Gesucht wird die **nächste** Gruppe über der
 * Rohdatenschicht, die ein Positionsplan als seine nennt. In der Dev-Anlage
 * liegt `LDG-ROH` in `LDG-HGB`, und `PLA-HGB` gilt für `LDG-HGB` — dieselbe
 * Antwort, die vorher von Hand in der Kontextzeile stand.
 *
 * **Gefunden wird von unten nach oben**, und die erste Gruppe mit Plan
 * gewinnt: `LDG-HGB` liegt seinerseits in `LDG-IFRS`, und beide führen einen
 * Plan — gemeint ist die nähere. Trägt keine Gruppe über der Schicht einen
 * Plan, kommt `null` zurück, und das Fenster sagt es (R144).
 */
export function berichtsebene(
  aufbau: Aufbaubild | null,
  plaene: Positionsplan[],
  lieferledger: string,
): string | null {
  if (!aufbau) {
    return null
  }

  const zeilen = [...aufbau.zeilen, ...aufbau.freie]
  const traegtPlan = (schluessel: string) =>
    plaene.some((p) => p.ledger.includes(schluessel))

  // Die Schicht selbst darf einen Plan tragen — dann ist sie die Antwort.
  if (traegtPlan(lieferledger)) {
    return lieferledger
  }

  let oben = zeilen.find((z) => z.schluessel === lieferledger)?.gruppe ?? null
  const gesehen = new Set<string>([lieferledger])

  while (oben !== null && !gesehen.has(oben)) {
    if (traegtPlan(oben)) {
      return oben
    }

    gesehen.add(oben)
    oben = zeilen.find((z) => z.schluessel === oben)?.gruppe ?? null
  }

  return null
}

/** Die Einträge eines Katalogs, die zur Suche passen — über Code und Namen. */
export function passende(vorrat: Katalogeintrag[], suche: string): Katalogeintrag[] {
  const gesucht = suche.trim().toLowerCase()

  return vorrat.filter(
    (k) =>
      gesucht.length === 0 ||
      k.code.toLowerCase().includes(gesucht) ||
      beschriftung(k.namen).toLowerCase().includes(gesucht),
  )
}

/**
 * Die Namen der Zielfelder, die dem Haus gehören und keinen eigenen tragen.
 *
 * **Die Dimensionen stehen nicht darin:** Sie heißen, wie der Kunde sie nennt,
 * und den Namen bringt das Zielfeld selbst mit (R522).
 */
const FELDNAMEN: Record<string, string> = {
  ic_partner: 'Partner',
  segment: 'Segment',
  funktionsbereich: 'Funktionsbereich',
}

/** Was aus einem offenen Konto geworden ist — es bleibt sichtbar stehen (R590). */
type Erledigt = { konto: string; art: 'zugeordnet' | 'angelegt' }

/** Welche Sorte Offenes der Reiter zeigt (R602). */
type Reiter = 'konten' | 'arten' | 'werte'

/**
 * Der Schlüssel der Gruppe *ohne Stelle im Plan* (R607).
 *
 * **Das @ hält sie von den Positionen fern:** Sie klappt über denselben Vorrat
 * auf wie jede Position, und ein Positionsschlüssel fängt nicht so an.
 */
const OHNE_STELLE = '@ohne-stelle'

export function Zuordnungsfenster({
  vorschau,
  einheit,
  periode,
  aufSchliessen,
}: {
  vorschau: Importvorschau
  einheit: string
  periode: number
  /** `geschrieben` sagt, ob die Datei neu gelesen werden muss (R591). */
  aufSchliessen: (geschrieben: boolean) => void
}) {
  /*
    **Jeder Griff fragt für sich** (R1180): Zuordnen verlangt ein anderes Recht
    als Anlegen. Was der Angemeldete nicht darf, steht nicht da — die offene
    Zeile bleibt lesbar, nur ohne den Griff.
  */
  const darf = useDarf()

  /*
    **Die Grenze zwischen Lieferung und Abschluss lässt sich ziehen** (7.2
    Abschnitt 41, R1262): 40 zu 60 passt für viele Lieferungen und für
    manche nicht — „Forderungen Fensterprobe Sonderfa…" stand links, und
    rechts war Platz.
  */
  const grenze = useBereichsgrenze('zuordnungsfenster')
  const bereiche = useRef<HTMLDivElement>(null)
  const offen = useMemo(() => offeneKonten(vorschau), [vorschau])
  const offeneArten = useMemo(() => offeneBewegungsarten(vorschau), [vorschau])
  const offeneFelder = useMemo(() => offeneWerte(vorschau), [vorschau])
  const offeneWertzahl = useMemo(
    () => [...offeneFelder.values()].reduce((summe, werte) => summe + werte.length, 0),
    [offeneFelder],
  )

  const [erledigt, setErledigt] = useState<Map<string, Erledigt>>(new Map())
  /** Was aus einem offenen Bewegungsartcode geworden ist (R595). */
  const [erledigteArten, setErledigteArten] = useState<Map<string, Erledigt>>(new Map())
  const [bewegungsarten, setBewegungsarten] = useState<Katalogeintrag[]>([])
  /** Für welchen Code gerade ein Kern gesucht wird — sonst `null`. */
  const [legtArtAn, setLegtArtAn] = useState<string | null>(null)
  /** Die offenen Werte je Vorratsfeld (R597). */
  const [erledigteWerte, setErledigteWerte] = useState<Map<string, Erledigt>>(new Map())
  const [einheiten, setEinheiten] = useState<Katalogeintrag[]>([])
  /** Für welches Feld und welchen Code gerade angelegt wird — `feld|code`. */
  const [legtWertAn, setLegtWertAn] = useState<string | null>(null)
  const [konten, setKonten] = useState<Katalogeintrag[]>([])
  /**
   * Die Rückfrage nach dem Monat (5.1 Abschnitt 3.51, R1171, R1173): War ein
   * gelieferter Wert schon einmal zugeordnet, verlangt der Server ihn
   * ausdrücklich — dieselbe Absage auf allen sechs Wegen dieses Fensters.
   */
  const monatsfrage = useMonatsfrage()
  const [ledgernamen, setLedgernamen] = useState<Katalogeintrag[]>([])

  /** Welche Sorte Offenes gerade dran ist (R602). */
  const [reiter, setReiter] = useState<Reiter>('konten')
  /** Die gewählten lokalen Konten — mehrere sind erlaubt (R605). */
  const [auswahl, setAuswahl] = useState<string[]>([])
  /** Welcher Bericht rechts steht (R603). */
  const [bericht, setBericht] = useState<Bericht>('bilanz')
  const [zielsuche, setZielsuche] = useState('')
  const [aufgeklappt, setAufgeklappt] = useState<ReadonlySet<string>>(new Set())
  /** Die angeklickte Position — sie zeigt das Einhängen als Symbol (R604). */
  const [zielposition, setZielposition] = useState<string | null>(null)

  /** Die offene Frage nach der Kontengruppe — nur ohne Geschwister (R308). */
  const [gruppenfrage, setGruppenfrage] = useState<{
    position: string
    /** **Der Name und nicht der Schlüssel** (R703) — der Dienst schickt beides. */
    positionsname: string
    vorrat: Gruppenangabe[]
    gruppe: string
  } | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)


  // **Der Katalog wird nach jedem Anlegen neu gelesen:** Ein eben erzeugtes
  // Konto stünde sonst mit seinem Schlüssel statt mit Nummer und Namen da.
  const [stand, setStand] = useState(0)

  useEffect(() => {
    // Namen und Wahlvorräte: Nachschlagen im Hintergrund (7.2 Abschnitt 30,
    // R1007) — still, mit einem zweiten Versuch; so auch die folgenden.
    let gilt = true
    imHintergrund(() => api.stammdatenkatalog())
      .then((k) => {
        if (!gilt) {
          return
        }
        setKonten(k.konten)
        setLedgernamen(k.ledger)
        setBewegungsarten(k.bewegungsarten)
      })
      .catch(() => undefined)

    return () => {
      gilt = false
    }
  }, [stand])

  // **Nur Kerne** (R594): Gewählt wird die Bedeutung, nicht ihre Merkmale.
  // Eine Auswahlliste — Abruf im Hintergrund, Ersatz: keine Wahl (R1211).
  const kerne = useHintergrundabruf(() => api.kernbewegungsarten(), [stand], KEINE_KERNE)

  // Die Vorräte der übrigen Zielfelder — dieselbe Auskunft, aus der auch das
  // Blatt der Datenquelle seine Auswahl baut (R522). Auswahllisten, also
  // im Hintergrund (R1211).
  const zielfelder = useHintergrundabruf(() => api.zielfelder(), [stand], KEINE_ZIELFELDER)

  useEffect(() => {
    let gilt = true
    imHintergrund(() => api.stammdatenkatalog())
      .then((k) => gilt && setEinheiten(k.einheiten))
      .catch(() => undefined)

    return () => {
      gilt = false
    }
  }, [stand])

  /*
    **Der Positionsplan wird abgeleitet** (R612, seit dem 2026-09-18
    nachmittags): Er hängt an der Berichtsebene und nicht an der
    Rohdatenschicht, in die die Lieferung bucht — und welche Berichtsebene das
    ist, steht im Aufbau der Bewertungsebenen. Bis dahin kam sie aus der
    Kontextzeile; die gibt es im Importbereich nicht mehr, und gebraucht wurde
    sie auch vorher nur, weil niemand nach oben gesehen hat.
  */
  /*
    **Haupt-, nicht Hintergrundabrufe** (7.2 Abschnitt 39, R1211): Von Aufbau
    und Plänen hängt die ganze rechte Hälfte ab. Bis zum 2026-10-06 liefen sie
    still, und ein Aussetzer des Dienstes ergab den Satz „Keine
    Bewertungsebene führt einen Positionsplan" — falsch, denn es gab einen.
  */
  const aufbauabruf = useAbruf(() => api.ledgeraufbau(), [stand])
  const planabruf = useAbruf(() => api.positionsplaene(), [stand])

  const ledger = useMemo(
    () => berichtsebene(aufbauabruf.wert, planabruf.wert ?? [], vorschau.ledger) ?? '',
    [aufbauabruf.wert, planabruf.wert, vorschau.ledger],
  )

  const gliederungsabruf = useAbruf(
    ledger ? () => api.gliederung(ledger, periode) : null,
    [ledger, periode, stand],
  )
  const gliederung = gliederungsabruf.wert

  /** Was rechts fehlt, weil es nicht kam — und was noch kommt (R1210). */
  const zielabrufe = [aufbauabruf, planabruf, gliederungsabruf]
  const zielausfall = zielabrufe.some((a) => a.ausgefallen)
  // Nur das erste Holen lässt die Fläche leer; nach einem Anlegen bleibt der
  // Baum stehen, bis der neue da ist.
  const zielLaedt = zielabrufe.some((a) => a.laedt && a.wert === null)
  const zielErneut = useMemo(
    () => ({ erneut: () => zielabrufe.filter((a) => a.ausgefallen).forEach((a) => a.erneut()) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aufbauabruf.ausgefallen, planabruf.ausgefallen, gliederungsabruf.ausgefallen],
  )

  /*
    **Offen wie im Bericht** (7.2 Abschnitt 55, R1857): jede Position mit
    Unterpositionen, die Konten erst auf Klick. Bis zum 2026-10-16 standen nur
    die Wurzeln offen (R603). Das geschieht einmal, wenn das Bild kommt — wer
    danach zuklappt, bleibt zugeklappt.
  */
  useEffect(() => {
    setAufgeklappt(berichtsstand(gliederung?.knoten ?? []))
  }, [gliederung])

  /** Beim Öffnen steht das erste offene Konto bereit (R605). */
  useEffect(() => {
    const erstes = offen[0]
    setAuswahl(erstes ? [erstes.lokalnummer] : [])
  }, [offen])

  // Escape schließt — das übliche Fenster hört darauf (R306, R1260).

  // Auch das Konto heißt, wie der Namensgeber es nennt (R1660).
  function kontoname(schluessel: string): string {
    return kontobezeichnung(schluessel)
  }

  // Bewegungsart und Bewertungsebene heißen, wie der Namensgeber sie nennt
  // (R1660) — bis zum 2026-10-12 aus eigenen Listen, mit dem Code als Rückfall.
  function ebenenname(schluessel: string): string {
    return bewertungsebenenname(schluessel)
  }

  const ledgername = useMemo(() => ebenenname(ledger), [ledgernamen, ledger])
  /** Die Schicht, in die gebucht wird — sie steht in der Absage (R144). */
  const schichtname = useMemo(() => ebenenname(vorschau.ledger), [ledgernamen, vorschau.ledger])

  /*
    **Zugeordnet wird auf ein Detail oder auf einen Kern** (3.12): Ein Kern
    *ist* eine Bewegungsart und darf gebucht werden. Deshalb steht hier der
    ganze Vorrat — anders als beim Anlegen, wo nur Kerne zur Wahl stehen.
  */
  // **Ohne Vortrag** (R1061): Startwert, Abschreibung Anfang und Übertrag der
  // Vormonate trägt CoKo selbst vor; ein Code darauf buchte sie doppelt.
  const bewegungsartenwahl = useMemo(() => ohneVortrag(bewegungsarten).map(ausStammdatum), [bewegungsarten])

  const kernwahl = useMemo(
    () =>
      kerne.map((k) => ({
        wert: k.schluessel,
        text: katalogbezeichnung(k),
        suchtext: [k.schluessel, k.code, ...Object.values(k.namen)].join(' '),
        stillgelegtAb: k.stillgelegtAb,
      })),
    [kerne],
  )

  /** Die Zeilen des Zielbaums, wie sie gerade zu sehen sind (R603). */
  const zielbaum = useMemo(
    () => zielzeilen(gliederung?.knoten ?? [], bericht, aufgeklappt, zielsuche),
    [gliederung, bericht, aufgeklappt, zielsuche],
  )

  /**
   * Der Kontenrahmen als flache Liste — **wenn es keinen Plan gibt** (3.15).
   *
   * **Ein Fenster, dessen rechte Hälfte leer bliebe, hätte den Bereichswechsel
   * wieder eingeführt**, den R589 gerade abgeschafft hat. Verschmelzen braucht
   * keinen Positionsplan; nur das Einhängen braucht einen.
   */
  const kontenrahmen = useMemo(
    () => passende(konten, zielsuche),
    [konten, zielsuche],
  )

  /**
   * Die Konzernkonten, die **an keiner Position dieses Plans hängen** (R607).
   *
   * **Der Durchgang an der Dev-Anlage hat sie gefunden:** Von fünfzig Konten
   * des Kontenrahmens stehen dreiundvierzig im Plan `PLA-HGB`. `6250
   * Reisekosten` ist eines der sieben übrigen — es gibt das Konto, der
   * Vorschlag nennt es, und im Baum war es **nicht zu finden**. Das alte
   * Fenster konnte darauf verschmelzen: Seine Klappliste ging über den ganzen
   * Kontenrahmen.
   *
   * **Sie sind kein Sonderfall, sondern eine stehende Gruppe.** Ein Konto, das
   * im Lauf entsteht, wird in **einen** Plan eingehängt und bleibt im zweiten
   * ausdrücklich uneingeordnet (3.4) — die Lücke ist sichtbar statt still. Wer
   * dann im zweiten Plan arbeitet, muss es trotzdem erreichen können.
   */
  const ohneStelle = useMemo(() => {
    if (gliederung === null) {
      return []
    }

    const imPlan = new Set(
      gliederung.knoten.flatMap((k) => k.konten.map((x) => x.schluessel)),
    )

    return passende(
      konten.filter((k) => !imPlan.has(k.schluessel)),
      zielsuche,
    )
  }, [konten, gliederung, zielsuche])

  /** Die gewählten Konten, die noch offen sind — sie bekommen den Griff (R605). */
  const griff = useMemo(
    () => offen.filter((o) => auswahl.includes(o.lokalnummer) && !erledigt.has(o.lokalnummer)),
    [offen, auswahl, erledigt],
  )

  /**
   * Nach dem Griff rückt die Auswahl weiter (R605).
   *
   * **Auf das nächste offene Konto**, von der zuletzt abgeräumten Zeile an und
   * notfalls von vorn: Wer dreißig abarbeitet, klickt links einmal und danach
   * nur noch rechts.
   */
  function rueckeWeiter(getan: string[], offenGeblieben: string[]) {
    if (offenGeblieben.length > 0) {
      setAuswahl(offenGeblieben)
      return
    }

    const fertig = new Set([...erledigt.keys(), ...getan])
    const letzte = Math.max(...getan.map((n) => offen.findIndex((o) => o.lokalnummer === n)))
    const rest = [...offen.slice(letzte + 1), ...offen.slice(0, letzte + 1)]
    const naechstes = rest.find((o) => !fertig.has(o.lokalnummer))
    setAuswahl(naechstes ? [naechstes.lokalnummer] : [])
  }

  /** Einen Eintrag zur Auswahl dazunehmen oder herausnehmen (R605, R306). */
  function nimmDazu(lokalnummer: string) {
    setAuswahl((bisher) =>
      bisher.includes(lokalnummer)
        ? bisher.filter((n) => n !== lokalnummer)
        : [...bisher, lokalnummer],
    )
  }

  /**
   * Verschmelzen — die gewählten Konten laufen auf ein bestehendes (R604).
   *
   * **Ein Griff, mehrere Zuordnungen** (R605): Jede wird einzeln geschrieben
   * und einzeln abgehakt. Bricht die siebte ab, bleiben die sechs davor
   * geschrieben — sie sind gültige Stammdaten und keine halbe Buchung.
   *
   * **Kein Vorschlag wird von selbst wirksam** (R5, R138): Auch der beste wird
   * hier angenommen und nicht angewendet.
   */
  async function verschmelze(ziel: string, nur?: OffenesKonto[], ab?: number) {
    const liste = nur ?? griff
    if (ziel === '' || liste.length === 0 || laeuft) {
      return
    }

    setLaeuft(true)
    setMeldung(null)

    const getan: string[] = []
    let fehler: string | null = null

    for (const konto of liste) {
      try {
        await api.zuordnen(einheit, konto.lokalnummer, konto.bezeichnung, ziel, periode, { wirksamAb: ab })
        getan.push(konto.lokalnummer)
        setErledigt((bisher) =>
          new Map(bisher).set(konto.lokalnummer, { konto: ziel, art: 'zugeordnet' }),
        )
      } catch (f) {
        // Fehlt der Monat, wird nach ihm gefragt — für dieses eine Konto;
        // die übrigen des Griffs bleiben gewählt (R1173).
        const absage = monatFehlt(f)
        if (absage) {
          monatsfrage.frage(absage, (monat) => void verschmelze(ziel, [konto], monat))
          break
        }
        // Nur die fachliche Absage steht hier; eine Störung meldet das
        // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
        fehler = fachlicherSatz(f)
        break
      }
    }

    setMeldung(fehler)
    rueckeWeiter(
      getan,
      liste.filter((k) => !getan.includes(k.lokalnummer)).map((k) => k.lokalnummer),
    )
    setLaeuft(false)
  }

  /**
   * Einhängen — aus den gewählten lokalen Konten werden Konzernkonten (R308).
   *
   * **Gefragt statt geraten:** Hängt unter der Position noch kein Konto, gibt
   * es keine Geschwister, von denen sich die Kontengruppe ableiten ließe. Dann
   * fragt das Fenster, statt eine Absage zu zeigen, die ein Feld verlangt, das
   * es nicht gab (R144).
   *
   * **Gefragt wird einmal, nicht zwölfmal** (R605): Die Antwort gilt für alle
   * Konten dieses Griffs — das erste angelegte ist für die übrigen ohnehin das
   * Geschwister, das sie brauchen.
   */
  async function haengeEin(position: string, gruppe: string | null = null, nur?: OffenesKonto[], ab?: number) {
    const liste = nur ?? griff
    if (position === '' || liste.length === 0 || laeuft) {
      return
    }

    setLaeuft(true)
    setMeldung(null)

    const getan: string[] = []
    let fehler: string | null = null
    let offenePlaene: string[] = []

    try {
      if (gruppe === null) {
        const vorab = await api.anlagevorschau(position, periode)
        if (vorab.gruppe === null) {
          setGruppenfrage({
            position,
            positionsname: zielname(vorab.positionscode, vorab.positionsnamen),
            vorrat: vorab.gruppenvorrat,
            gruppe: '',
          })
          return
        }
      }

      for (const konto of liste) {
        const anlage = await api.kontoErzeugen(
          einheit,
          konto.lokalnummer,
          konto.bezeichnung,
          position,
          gruppe,
          periode,
          ab,
        )

        getan.push(konto.lokalnummer)
        offenePlaene = anlage.offenePlaene
        setErledigt((bisher) =>
          new Map(bisher).set(konto.lokalnummer, { konto: anlage.konto, art: 'angelegt' }),
        )
      }

      setGruppenfrage(null)
    } catch (f) {
      const absage = monatFehlt(f)
      const dieses = liste[getan.length]
      if (absage && dieses) {
        monatsfrage.frage(absage, (monat) => void haengeEin(position, gruppe, [dieses], monat))
      } else {
        fehler = fachlicherSatz(f)
      }
    } finally {
      setMeldung(
        fehler ??
          (offenePlaene.length > 0
            ? t('mapping.offeneplaene', { plaene: offenePlaene.join(', ') })
            : null),
      )

      if (getan.length > 0) {
        rueckeWeiter(
          getan,
          liste.filter((k) => !getan.includes(k.lokalnummer)).map((k) => k.lokalnummer),
        )
        setStand((n) => n + 1)
      }

      setLaeuft(false)
    }
  }

  /**
   * Einen gelieferten Bewegungsartcode zuordnen (R593).
   *
   * **Derselbe Weg wie beim Konto daneben**, und das ist der Punkt des
   * Abschnitts: Der Anwender hat eine Lieferung, nicht zwei Probleme (R595).
   */
  async function legeArt(offeneArt: OffeneBewegungsart, ziel: string, ab?: number) {
    if (ziel === '') {
      return
    }

    setLaeuft(true)
    setMeldung(null)

    try {
      await api.bewegungsartZuordnen(einheit, offeneArt.code, offeneArt.bezeichnung, ziel, periode, ab)
      setErledigteArten((bisher) =>
        new Map(bisher).set(offeneArt.code, { konto: ziel, art: 'zugeordnet' }),
      )
      setLegtArtAn(null)
    } catch (f) {
      const absage = monatFehlt(f)
      if (absage) {
        monatsfrage.frage(absage, (monat) => void legeArt(offeneArt, ziel, monat))
        return
      }
      setMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Aus dem gelieferten Code eine Bewegungsart unter ihrem Kern machen (R594).
   *
   * **Gewählt wird der Kern, nicht die Eigenschaften.** Spiegel, KFR-Wirkung,
   * Zahlungswirksamkeit und Kurstyp erbt das Neue (R31) — ein Blatt, das sie
   * hier abfragte, lüde dazu ein, sie abweichend zu beantworten.
   */
  async function erzeugeArt(offeneArt: OffeneBewegungsart, kern: string, ab?: number) {
    if (kern === '') {
      return
    }

    setLaeuft(true)
    setMeldung(null)

    try {
      const anlage = await api.bewegungsartErzeugen(
        einheit,
        offeneArt.code,
        offeneArt.bezeichnung,
        kern,
        periode,
        ab,
      )

      setErledigteArten((bisher) =>
        new Map(bisher).set(offeneArt.code, { konto: anlage.bewegungsart, art: 'angelegt' }),
      )
      setLegtArtAn(null)
      // Die Tat und ihr Gegenstand, sonst nichts (7.2 Abschnitt 34, R1131) —
      // woher die Bedeutung kommt, steht an der Zeile, die jetzt „neu angelegt
      // als …" trägt.
      meldeErfolg(t('erfolg.bewegungsart.angelegt'))
      setMeldung(null)
      setStand((n) => n + 1)
    } catch (f) {
      const absage = monatFehlt(f)
      if (absage) {
        monatsfrage.frage(absage, (monat) => void erzeugeArt(offeneArt, kern, monat))
        return
      }
      setMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Der Vorrat eines Zielfelds für die Auswahl (R597).
   *
   * **Beim Partner sind es zwei** (R600): die Konzerngesellschaften und die
   * Partnercodes. Beides sind gültige Partner; nur die Gesellschaft wird
   * konsolidiert, und das entscheidet nicht dieses Fenster.
   */
  function vorratVon(feld: string) {
    const ziel = zielfelder.find((z) => z.name === feld)
    const aus = (ziel?.werte ?? []).map((v: Vorratswert) => ({
      wert: v.schluessel,
      // **Nicht „ACME — ACME“**: Ein Vorratswert, der aus einem gelieferten
      // Wert entstanden ist, trägt ihn oft als Namen — dann steht er einmal da.
      text: beschriftung(v.namen) && beschriftung(v.namen) !== v.code
        ? katalogbezeichnung(v)
        : v.code,
      suchtext: [v.schluessel, v.code, ...Object.values(v.namen)].join(' '),
      stillgelegtAb: v.stillgelegtAb,
    }))

    return feld === 'ic_partner' ? [...einheiten.map(ausStammdatum), ...aus] : aus
  }

  /** Der Name eines Zielfelds, wie ihn auch das Blatt der Quelle zeigt. */
  function feldname(feld: string): string {
    const ziel = zielfelder.find((z) => z.name === feld)
    const eigen = ziel?.namen ? beschriftung(ziel.namen) : ''
    return eigen || FELDNAMEN[feld] || feld
  }

  function wertname(feld: string, schluessel: string): string {
    const treffer = vorratVon(feld).find((v: { wert: string }) => v.wert === schluessel)
    return treffer?.text ?? schluessel
  }

  /** Einen gelieferten Wert zuordnen (R597). */
  async function legeWert(feld: string, wert: OffeneBewegungsart, ziel: string, ab?: number) {
    if (ziel === '') {
      return
    }

    setLaeuft(true)
    setMeldung(null)

    try {
      await api.wertZuordnen(einheit, feld, wert.code, wert.bezeichnung, ziel, periode, ab)
      setErledigteWerte((bisher) =>
        new Map(bisher).set(`${feld}|${wert.code}`, { konto: ziel, art: 'zugeordnet' }),
      )
      setLegtWertAn(null)
    } catch (f) {
      const absage = monatFehlt(f)
      if (absage) {
        monatsfrage.frage(absage, (monat) => void legeWert(feld, wert, ziel, monat))
        return
      }
      setMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Aus dem gelieferten Wert einen Vorratswert machen (R599).
   *
   * **Nur wo der Vorrat mitwächst** — Partnercode, Segment, Dimensionswert.
   * Der Knopf steht anderswo gar nicht erst da.
   */
  async function erzeugeWert(feld: string, wert: OffeneBewegungsart, ab?: number) {
    setLaeuft(true)
    setMeldung(null)

    try {
      const anlage = await api.wertErzeugen(einheit, feld, wert.code, wert.bezeichnung, periode, ab)
      setErledigteWerte((bisher) =>
        new Map(bisher).set(`${feld}|${wert.code}`, { konto: anlage.schluessel, art: 'angelegt' }),
      )
      meldeErfolg(t('erfolg.wert.angelegt'))
      setMeldung(null)
      setStand((n) => n + 1)
    } catch (f) {
      const absage = monatFehlt(f)
      if (absage) {
        monatsfrage.frage(absage, (monat) => void erzeugeWert(feld, wert, monat))
        return
      }
      setMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  /** Eine Position auf- oder zuklappen (R603). */
  function klappe(schluessel: string) {
    setAufgeklappt((bisher) => {
      const neu = new Set(bisher)
      if (!neu.delete(schluessel)) {
        neu.add(schluessel)
      }
      return neu
    })
  }

  const geschrieben = erledigt.size > 0 || erledigteArten.size > 0 || erledigteWerte.size > 0

  /*
    **Die Reiter stehen nur für Sorten, die es gibt** (R602): Eine Lieferung
    mit lauter offenen Konten zeigt einen Reiter und nicht drei, von denen
    zwei leer sind.
  */
  const alleSorten: { name: Reiter; text: string; anzahl: number }[] = [
    { name: 'konten', text: t('import.zuordnung.abschnitt.konten'), anzahl: offen.length },
    {
      name: 'arten',
      text: t('import.zuordnung.abschnitt.bewegungsarten'),
      anzahl: offeneArten.length,
    },
    { name: 'werte', text: t('import.zuordnung.abschnitt.werte'), anzahl: offeneWertzahl },
  ]

  const sorten = alleSorten.filter((r) => r.anzahl > 0)

  // Bleibt keine Sorte übrig, an der das Fenster geöffnet wurde, rückt es auf
  // die erste, die es gibt — ein leerer Bereich beantwortet keine Frage.
  const offenerReiter = sorten.some((r) => r.name === reiter)
    ? reiter
    : sorten[0]?.name ?? 'konten'

  /** Der Titel eines Ziels — der volle Text steht im Tooltip (R606). */
  function voll(code: string, name: string): string {
    return name && name !== code ? `${code} ${name}` : code
  }

  /**
   * Wie ein Ziel heißt — **der Name, nicht der Schlüssel** (R703).
   *
   * `voll()` darüber gilt weiter der **linken** Hälfte: Dort steht der Code
   * der Lieferung, und der gehört dem Vorsystem (R679). Rechts steht unser
   * eigener Schlüssel, und der sagt einem Menschen nichts — bei einem Konto
   * stand die Nummer dadurch zweimal: *„KTO-0210 · 0210 Grundstücke und
   * Bauten"*. Ohne Bezeichnung bleibt er stehen, damit keine Zeile und kein
   * Satz leer ist (R238).
   */
  function zielname(code: string, namen: Beschriftung): string {
    return beschriftung(namen) || code
  }

  /**
   * Was das Symbol an der Position tut — **ein Konto oder mehrere**.
   *
   * **„1 Konten unter A.II.1 neu anlegen" stand am 2026-09-18 im Durchgang da.**
   * Die Sprachdatei führt dafür ein Paar, wie das Stammdatenblatt seit dem
   * Vormittag und der Knopf über der Vorschau seit 3.11.
   */
  function einhaengetext(positionscode: string): string {
    return t(
      griff.length === 1 ? 'import.zuordnung.einhaengen.eins' : 'import.zuordnung.einhaengen.mehrere',
      { position: positionscode, anzahl: anzahlwort(griff.length) },
    )
  }

  /*
    **Das übliche Fenster, bildschirmfüllend** (7.2 Abschnitt 41, R1260,
    R1261): verschieben am Kopf, groß und klein im Kopf, die Ecke zieht,
    Esc und Kreuz schließen. Bis zum 2026-10-06 war es eigens gebaut und
    konnte nichts davon.
  */
  return (
    <Dialogfenster
      titel={t('import.zuordnung.titel')}
      weite="breit"
      aufSchliessen={() => aufSchliessen(geschrieben)}
      kopfzusatz={
        /*
          **Ein Fortschritt für alles Offene** (R595, R602): Der Anwender hat
          eine Lieferung, nicht drei Probleme — und die Frage „bin ich
          fertig?" hätte sonst drei Antworten, die man addieren muss.
        */
        <span className="fortschritt" aria-live="polite">
          {t('import.zuordnung.fortschritt', {
            erledigt: anzahlwort(erledigt.size + erledigteArten.size + erledigteWerte.size),
            gesamt: anzahlwort(offen.length + offeneArten.length + offeneWertzahl),
          })}
        </span>
      }
      fuss={
        <button type="button" className="haupt" onClick={() => aufSchliessen(geschrieben)}>
          {t(geschrieben ? 'import.zuordnung.schliessen' : 'import.zuordnung.schliessen.ohne')}
        </button>
      }
    >
      <div className="zuordnungsfenster">

            {/*
              **Die Klammer steht unveränderlich da** — Gesellschaft, Periode
              und die Bewertungsebene, in deren Positionsplan ein neues Konto
              gehängt wird. Sie sind Angaben des Laufs (R589); eine Auswahl
              daraus zu machen hieße, eine Wahl anzubieten, die die Lieferung
              schon getroffen hat.
            */}
            <p className="nachweisfilter">
              {t('import.zuordnung.klammer', {
                datei: vorschau.dateiname,
                ledger: ledgername,
              })}
            </p>

        {/*
          **Eine Leiste für die Sorten des Offenen** (R602). Der Baum gehört
          den Konten: Eine Bewegungsart hat keine Stelle im Abschluss, sie hat
          einen Kern — und ein Segment hat einen Vorrat.
        */}
        {sorten.length > 1 && (
          <nav className="reiterleiste" aria-label={t('import.zuordnung.sorten')}>
            {sorten.map((r) => (
              <button
                key={r.name}
                type="button"
                className={`reiter ${offenerReiter === r.name ? 'aktiv' : ''}`.trim()}
                aria-pressed={offenerReiter === r.name}
                onClick={() => setReiter(r.name)}
              >
                {r.text} <span className="zahl">{anzahlwort(r.anzahl)}</span>
              </button>
            ))}
          </nav>
        )}

        {meldung && <p className="hinweis">{meldung}</p>}

        {gruppenfrage && (
          <div className="gruppenfrage">
            <p className="erlaeuterung">
              {t('mapping.gruppe.frage', {
                position: gruppenfrage.positionsname,
                lokal: griff.map((k) => k.lokalnummer).join(', '),
              })}
            </p>
            <p className="handreichung">
              <Auswahlfeld
                wert={gruppenfrage.gruppe}
                beschriftung={t('mapping.gruppe.wahl')}
                leer={t('mapping.gruppe.wahl')}
                eintraege={gruppenfrage.vorrat.map((g) => ({
                  wert: g.schluessel,
                  text: `${g.code} ${beschriftung(g.namen)}`,
                  suchtext: [g.schluessel, ...Object.values(g.namen)].join(' '),
                  stillgelegtAb: g.stillgelegtAb,
                }))}
                aufWahl={(gewaehlt) => setGruppenfrage({ ...gruppenfrage, gruppe: gewaehlt })}
              />{' '}
              <button
                type="button"
                disabled={laeuft || gruppenfrage.gruppe === ''}
                onClick={() => void haengeEin(gruppenfrage.position, gruppenfrage.gruppe)}
              >
                {t('mapping.gruppe.anlegen')}
              </button>{' '}
              <button type="button" onClick={() => setGruppenfrage(null)}>
                {t('mapping.gruppe.abbrechen')}
              </button>
            </p>
          </div>
        )}

        {offen.length === 0 && offeneArten.length === 0 && offeneWertzahl === 0 && (
          <p className="hinweis">{t('import.zuordnung.leer')}</p>
        )}

        {/*
          **Links die Lieferung, rechts der Abschluss** (R602). Wer ein fremdes
          Konto einordnet, sucht nicht nach einer Nummer, sondern nach der
          Stelle im Abschluss, an die es gehört (3.4) — und die zeigt nur ein
          Baum, keine Klappliste.
        */}
        {offenerReiter === 'konten' && offen.length > 0 && (
          <div ref={bereiche} className="zuordnungsbereiche" style={{ gridTemplateColumns: grenze.spalten }}>
            <section className="quellbereich" aria-label={t('import.zuordnung.quelle')}>
              <h3>
                {t('import.zuordnung.quelle')}
                {griff.length > 1 && (
                  <span className="zahl">{t('import.zuordnung.gewaehlt', { anzahl: anzahlwort(griff.length) })}</span>
                )}
              </h3>

              <ul className="quellliste offeneliste">
                {offen.map((o) => {
                  const fertig = erledigt.get(o.lokalnummer)
                  const gewaehlt = auswahl.includes(o.lokalnummer)

                  return (
                    <li
                      key={o.lokalnummer}
                      aria-current={gewaehlt && !fertig ? true : undefined}
                      className={`${fertig ? 'erledigt' : ''} ${gewaehlt && !fertig ? 'gewaehlt' : ''}`.trim() || undefined}
                    >
                      {/*
                        **Das Kästchen nimmt dazu, der Klick wählt allein**
                        (R605). Beides geht ohne Maus (R306).
                      */}
                      {!fertig && (
                        <input
                          type="checkbox"
                          className="mitnehmen"
                          checked={gewaehlt}
                          disabled={laeuft}
                          aria-label={t('import.zuordnung.dazunehmen', { konto: o.lokalnummer })}
                          onChange={() => nimmDazu(o.lokalnummer)}
                        />
                      )}

                      <button
                        type="button"
                        className="quellzeile konto"
                        disabled={laeuft || fertig !== undefined}
                        draggable={fertig === undefined}
                        aria-pressed={gewaehlt && !fertig}
                        title={voll(o.lokalnummer, o.bezeichnung ?? '')}
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', o.lokalnummer)
                          if (!gewaehlt) {
                            setAuswahl([o.lokalnummer])
                          }
                        }}
                        onClick={() => setAuswahl([o.lokalnummer])}
                      >
                        <span className="code">{o.lokalnummer}</span>
                        <span className="name">{o.bezeichnung ?? '—'}</span>
                        {o.zeilen > 1 && (
                          <span className="gedaempft">
                            {t('import.zuordnung.zeilen', { anzahl: anzahlwort(o.zeilen) })}
                          </span>
                        )}
                      </button>

                      {/*
                        **Ein erledigtes Konto verschwindet nicht** (R590): Was
                        man eben entschieden hat, ist die Grundlage, auf der man
                        die nächste Entscheidung prüft — *4900* und *4910* gehen
                        beide auf KTO-6900, und das sieht nur, wer die erste
                        Zuordnung noch sieht.
                      */}
                      {fertig ? (
                        <span className="tat" title={kontoname(fertig.konto)}>
                          <span className="haken" aria-hidden="true">
                            ✓
                          </span>{' '}
                          {t(
                            fertig.art === 'angelegt'
                              ? 'import.zuordnung.angelegt'
                              : 'import.zuordnung.zugeordnet',
                            { konto: kontoname(fertig.konto) },
                          )}
                        </span>
                      ) : (
                        // Jeder Vorschlag nennt seine Quelle (R138) — sonst wird
                        // geklickt statt gelesen. In einer Zeile ohne Umbruch
                        // steht sie im Tooltip (R606).
                        darf('zuordnen') && o.vorschlag && (
                          <button
                            type="button"
                            className="schmal vorschlag"
                            disabled={laeuft}
                            aria-label={t('import.zuordnen', {
                              konto: o.vorschlagbezeichnung ?? o.vorschlag,
                            })}
                            title={
                              t('import.zuordnen', {
                                konto: o.vorschlagbezeichnung ?? o.vorschlag,
                              }) + (o.vorschlagquelle ? ` (${o.vorschlagquelle})` : '')
                            }
                            onClick={() => void verschmelze(o.vorschlag!, [o])}
                          >
                            ↦ {o.vorschlagbezeichnung ?? o.vorschlag}
                          </button>
                        )
                      )}

                      {/*
                        **Wohin ein Konto gehört, sagt oft erst das Vorzeichen**
                        (5.1 Abschnitt 3.57, R1214): 2950 Rückstellung mit
                        −4.000,00 gehört auf die Passivseite. Der Betrag wird nie
                        abgeschnitten; Platz gibt die Bezeichnung ab.
                      */}
                      <span className={`betrag${o.summe !== null && o.summe < 0 ? ' minus' : ''}`}>
                        {o.summe === null ? '—' : <Betrag wert={o.summe} />}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </section>

            <Bereichsgrenze
              rahmen={bereiche}
              anteil={grenze.anteil}
              setze={grenze.setze}
              beschriftung={t('import.zuordnung.grenze')}
            />

            <section className="zielbereich" aria-label={t('import.zuordnung.ziel')}>
              <div className="zielleiste">
                {/*
                  **Gewechselt wird zwischen Bilanz und GuV** (R603). Der
                  Positionsplan steht nicht zur Wahl: Er hängt an der
                  Bewertungsebene der Kontextzeile, und die steht im Kopf.
                */}
                <div className="berichtswahl" role="group" aria-label={t('import.zuordnung.bericht')}>
                  {(['bilanz', 'guv'] as Bericht[]).map((b) => (
                    <button
                      key={b}
                      type="button"
                      className={`reiter ${bericht === b ? 'aktiv' : ''}`.trim()}
                      aria-pressed={bericht === b}
                      onClick={() => setBericht(b)}
                    >
                      {t(`import.zuordnung.bericht.${b}`)}
                    </button>
                  ))}
                </div>

                <input
                  type="search"
                  value={zielsuche}
                  onChange={(e) => setZielsuche(e.target.value)}
                  placeholder={t('import.zuordnung.ziel.suche')}
                  aria-label={t('import.zuordnung.ziel.suche')}
                />
              </div>

              {/*
                **Ohne Positionsplan steht hier der Kontenrahmen** (3.15):
                Verschmelzen braucht keinen Plan, nur das Einhängen braucht
                einen — und warum es fehlt, sagt der Satz darüber (R144).
                **Aber nur, wenn feststeht, dass es keinen gibt** (R1210):
                Kam er nicht, steht da „Ließ sich nicht laden"; kommt er
                noch, steht nichts.
              */}
              {zielausfall ? (
                <Abrufausfall abruf={zielErneut} />
              ) : zielLaedt ? null : gliederung === null ? (
                <>
                  <p className="hinweis">{t('import.zuordnung.ohneplan', { ledger: schichtname })}</p>
                  {darf('zuordnen') && (
                    <ul className="zielbaum flach">
                      {kontenrahmen.map((k) => (
                        <li key={k.schluessel}>
                          <button
                            type="button"
                            className="zielkonto"
                            disabled={laeuft || griff.length === 0}
                            title={zielname(k.code, k.namen)}
                            onClick={() => void verschmelze(k.schluessel)}
                          >
                            <span className="name">{zielname(k.code, k.namen)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                /* **Gezeichnet wie der Bericht** (7.2 Abschnitt 55, R1855, R1856). */
                <ul className="zielbaum berichtsbaum">
                  {zielbaum.flatMap((z) => [
                    <Baumzeile
                      key={z.knoten.schluessel}
                      art="position"
                      ebene={z.knoten.ebene}
                      rang={z.knoten}
                      klasse={zielposition === z.knoten.schluessel ? 'gewaehlt' : undefined}
                      aria-current={zielposition === z.knoten.schluessel ? true : undefined}
                      pfeil={{
                        offen: z.offen,
                        hatKinder: z.hatKinder,
                        beschriftung: t(z.offen ? 'import.zuordnung.zuklappen' : 'import.zuordnung.aufklappen', {
                          position: zielname(z.knoten.code, z.knoten.namen),
                        }),
                        aufKlappen: () => klappe(z.knoten.schluessel),
                      }}
                      // Ohne das Recht zum Anlegen nimmt die Position nichts an (R1180).
                      onDragOver={(e) => darf('kontoErzeugen') && !z.knoten.istRechenposition && e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault()
                        if (darf('kontoErzeugen') && !z.knoten.istRechenposition) {
                          void haengeEin(z.knoten.schluessel)
                        }
                      }}
                    >
                      {/*
                        **Der Klick wählt, er legt nicht an** (R604): Hier
                        entsteht ein neues Stammdatum mit Nummer, Bezeichnung,
                        Kontoart und Kontengruppe. Das geschieht nicht aus
                        Versehen, sondern über das Symbol daneben.
                      */}
                      <button
                        type="button"
                        className="zeileninhalt position"
                        disabled={laeuft || z.knoten.istRechenposition}
                        aria-pressed={zielposition === z.knoten.schluessel}
                        title={
                          z.knoten.istRechenposition
                            ? t('mapping.rechenposition')
                            : zielname(z.knoten.code, z.knoten.namen)
                        }
                        onClick={() => setZielposition(z.knoten.schluessel)}
                      >
                        <span className="name">{zielname(z.knoten.code, z.knoten.namen)}</span>
                      </button>

                      {darf('kontoErzeugen') && zielposition === z.knoten.schluessel && !z.knoten.istRechenposition && (
                        <button
                          type="button"
                          className="einhaengen"
                          disabled={laeuft || griff.length === 0}
                          aria-label={einhaengetext(zielname(z.knoten.code, z.knoten.namen))}
                          title={einhaengetext(zielname(z.knoten.code, z.knoten.namen))}
                          onClick={() => void haengeEin(z.knoten.schluessel)}
                        >
                          ＋
                        </button>
                      )}
                    </Baumzeile>,
                    ...(darf('zuordnen')
                      ? z.konten.map((k) => (
                          <Baumzeile key={`${z.knoten.schluessel}:${k.schluessel}`} art="konto" ebene={z.knoten.ebene}>
                            {/*
                              **Ein Klick auf ein Zielkonto verschmilzt
                              sofort** (R604): Das Ziel gibt es schon, es ist
                              der häufige und leichte Fall — ein
                              Zwischenschritt wäre nur ein Klick mehr.
                            */}
                            <button
                              type="button"
                              className="zeileninhalt zielkonto"
                              disabled={laeuft || griff.length === 0}
                              title={
                                griff.length === 0
                                  ? t('import.zuordnung.erstwaehlen')
                                  : t('mapping.hierablegen', {
                                      lokal: griff.map((g) => g.lokalnummer).join(', '),
                                    })
                              }
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => {
                                e.preventDefault()
                                void verschmelze(k.schluessel)
                              }}
                              onClick={() => void verschmelze(k.schluessel)}
                            >
                              <span className="name">{zielname(k.nummer, k.namen)}</span>
                            </button>
                          </Baumzeile>
                        ))
                      : []),
                  ])}

                  {/*
                    **Ein Konto ohne Stelle im Plan bleibt erreichbar** (R607).
                    Es hängt an keiner Position und steht deshalb in keinem Ast
                    — verschmelzen darf man trotzdem darauf, und ohne diese
                    Gruppe wäre es nur über den Vorschlag zu treffen. Ein Ast,
                    den der Bericht nicht kennt: ohne Rang, zu (R1857).
                  */}
                  {darf('zuordnen') && ohneStelle.length > 0 && (
                    <Baumzeile
                      art="position"
                      ebene={0}
                      klasse="ohnestelle"
                      pfeil={{
                        offen: aufgeklappt.has(OHNE_STELLE),
                        hatKinder: true,
                        beschriftung: t(
                          aufgeklappt.has(OHNE_STELLE) ? 'import.zuordnung.zuklappen' : 'import.zuordnung.aufklappen',
                          { position: t('import.zuordnung.ziel.ohnestelle', { anzahl: anzahlwort(ohneStelle.length) }) },
                        ),
                        aufKlappen: () => klappe(OHNE_STELLE),
                      }}
                    >
                      <span className="zeileninhalt gruppe" title={t('import.zuordnung.ziel.ohnestelle.grund')}>
                        {t('import.zuordnung.ziel.ohnestelle', { anzahl: anzahlwort(ohneStelle.length) })}
                      </span>
                    </Baumzeile>
                  )}
                  {darf('zuordnen') &&
                    (aufgeklappt.has(OHNE_STELLE) || zielsuche.trim().length > 0) &&
                    ohneStelle.map((k) => (
                      <Baumzeile key={`${OHNE_STELLE}:${k.schluessel}`} art="konto" ebene={0}>
                        <button
                          type="button"
                          className="zeileninhalt zielkonto"
                          disabled={laeuft || griff.length === 0}
                          title={
                            griff.length === 0
                              ? t('import.zuordnung.erstwaehlen')
                              : t('mapping.hierablegen', {
                                  lokal: griff.map((g) => g.lokalnummer).join(', '),
                                })
                          }
                          onClick={() => void verschmelze(k.schluessel)}
                        >
                          <span className="name">{zielname(k.code, k.namen)}</span>
                        </button>
                      </Baumzeile>
                    ))}

                  {zielbaum.length === 0 && ohneStelle.length === 0 && (
                    <li className="hinweis">{t('import.zuordnung.ziel.keintreffer')}</li>
                  )}
                </ul>
              )}
            </section>
          </div>
        )}

        {/*
          **Der zweite offene Punkt der Lieferung** (R593, R595). Bis zum
          2026-09-18 stand hier nichts: Ein gelieferter Code, der auf keine
          Bewegungsart zeigte, war ein Befund an der Zeile, und geholfen hat
          nur, die Datei zu ändern.

          **Der Baum gehört den Konten** (R602): Eine Bewegungsart hat keine
          Stelle im Abschluss, sie hat einen Kern.
        */}
        {offenerReiter === 'arten' && offeneArten.length > 0 && (
          <ul className="offeneliste">
            {offeneArten.map((a) => {
              const fertig = erledigteArten.get(a.code)

              return (
                <li key={a.code} className={fertig ? 'erledigt' : undefined}>
                  <div className="konto">
                    <span className="code">{a.code}</span>
                    <span className="name">{a.bezeichnung ?? '—'}</span>
                    {a.zeilen > 1 && (
                      <span className="gedaempft">
                        {t('import.zuordnung.zeilen', { anzahl: anzahlwort(a.zeilen) })}
                      </span>
                    )}
                  </div>

                  {fertig ? (
                    <p className="tat">
                      <span className="haken" aria-hidden="true">
                        ✓
                      </span>{' '}
                      {t(
                        fertig.art === 'angelegt'
                          ? 'import.zuordnung.art.angelegt'
                          : 'import.zuordnung.art.zugeordnet',
                        { konto: bewegungsartname(fertig.konto) },
                      )}
                    </p>
                  ) : (
                    <div className="handgriffe">
                      {darf('bewegungsartZuordnen') && a.vorschlag && (
                        <span className="vorschlag">
                          <button
                            type="button"
                            className="schmal"
                            disabled={laeuft}
                            onClick={() => void legeArt(a, a.vorschlag!)}
                          >
                            {t('import.zuordnen', {
                              konto: a.vorschlagbezeichnung ?? a.vorschlag,
                            })}
                          </button>
                          {a.vorschlagquelle && (
                            <span className="gedaempft"> ({a.vorschlagquelle})</span>
                          )}
                        </span>
                      )}

                      {/*
                        **Der ganze Vorrat**, Kerne und Details: Ein Kern ist
                        eine Bewegungsart und darf gebucht werden.
                      */}
                      {darf('bewegungsartZuordnen') && (
                        <Auswahlfeld
                          wert=""
                          eintraege={bewegungsartenwahl}
                          leer={t('import.zuordnung.art.waehlen')}
                          beschriftung={t('import.zuordnung.art.waehlen')}
                          abgeschaltet={laeuft}
                          aufWahl={(ziel) => void legeArt(a, ziel)}
                        />
                      )}

                      {darf('bewegungsartErzeugen') &&
                        (legtArtAn === a.code ? (
                          <span className="anlage">
                            {/* **Nur Kerne** (R594): einordnen, nicht ausfüllen. */}
                            <Auswahlfeld
                              wert=""
                              eintraege={kernwahl}
                              leer={t('import.zuordnung.art.kern')}
                              beschriftung={t('import.zuordnung.art.kern')}
                              abgeschaltet={laeuft}
                              aufWahl={(kern) => void erzeugeArt(a, kern)}
                            />{' '}
                            <button
                              type="button"
                              className="schmal"
                              onClick={() => setLegtArtAn(null)}
                            >
                              {t('mapping.gruppe.abbrechen')}
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="schmal"
                            disabled={laeuft || kerne.length === 0}
                            onClick={() => setLegtArtAn(a.code)}
                          >
                            {t('import.zuordnung.art.neuanlegen')}
                          </button>
                        ))}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {/*
          **Ein Abschnitt je Feld mit offenen Werten** (R597, R595). Leere
          Abschnitte gibt es nicht: Eine Lieferung mit zwei offenen Segmenten
          zeigt einen, nicht elf.
        */}
        {offenerReiter === 'werte' &&
          [...offeneFelder].map(([feld, werte]) => (
            <div key={feld}>
              <h3>{feldname(feld)}</h3>

              {/*
                **Wo der Vorrat nicht mitwächst, steht der Grund** (R599, R144):
                Der Funktionsbereich trägt Bedeutung für die Gewinn- und
                Verlustrechnung nach dem Umsatzkostenverfahren — was Bedeutung
                trägt, entsteht in der Pflege und nicht nebenbei.
              */}
              {darf('wertErzeugen') && !vorratWaechstMit(feld) && (
                <p className="erlaeuterung">{t('import.zuordnung.wert.keinvorrat')}</p>
              )}

              <ul className="offeneliste">
                {werte.map((w) => {
                  const fertig = erledigteWerte.get(`${feld}|${w.code}`)

                  return (
                    <li key={w.code} className={fertig ? 'erledigt' : undefined}>
                      <div className="konto">
                        <span className="code">{w.code}</span>
                        <span className="name">{w.bezeichnung ?? '—'}</span>
                        {w.zeilen > 1 && (
                          <span className="gedaempft">
                            {t('import.zuordnung.zeilen', { anzahl: anzahlwort(w.zeilen) })}
                          </span>
                        )}
                      </div>

                      {fertig ? (
                        <p className="tat">
                          <span className="haken" aria-hidden="true">
                            ✓
                          </span>{' '}
                          {t(
                            fertig.art === 'angelegt'
                              ? 'import.zuordnung.wert.angelegt'
                              : 'import.zuordnung.wert.zugeordnet',
                            { konto: wertname(feld, fertig.konto) },
                          )}
                        </p>
                      ) : (
                        <div className="handgriffe">
                          {darf('wertZuordnen') && w.vorschlag && (
                            <span className="vorschlag">
                              <button
                                type="button"
                                className="schmal"
                                disabled={laeuft}
                                onClick={() => void legeWert(feld, w, w.vorschlag!)}
                              >
                                {t('import.zuordnen', {
                                  konto: w.vorschlagbezeichnung ?? w.vorschlag,
                                })}
                              </button>
                              {w.vorschlagquelle && (
                                <span className="gedaempft"> ({w.vorschlagquelle})</span>
                              )}
                            </span>
                          )}

                          {darf('wertZuordnen') && (
                            <Auswahlfeld
                              wert=""
                              eintraege={vorratVon(feld)}
                              leer={t('import.zuordnung.wert.waehlen')}
                              beschriftung={t('import.zuordnung.wert.waehlen')}
                              abgeschaltet={laeuft}
                              aufWahl={(ziel) => void legeWert(feld, w, ziel)}
                            />
                          )}

                          {darf('wertErzeugen') && vorratWaechstMit(feld) && (
                            <button
                              type="button"
                              className="schmal"
                              disabled={laeuft || legtWertAn === `${feld}|${w.code}`}
                              onClick={() => {
                                setLegtWertAn(`${feld}|${w.code}`)
                                void erzeugeWert(feld, w)
                              }}
                            >
                              {t('import.zuordnung.wert.neuanlegen')}
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}

        {/*
          **Geschlossen wird auch mit offenen Konten** (R144): Wer nach zwölf
          von dreißig aufhört, schließt; die zwölf sind geschrieben. Ein
          Fenster, das erst bei Vollständigkeit aufgeht, wäre eine Falle.

          **Gelesen wird einmal, hier** (R591): Die Vorschau entsteht, indem
          die Datei erneut hochgeladen, gelesen, verprobt und gegen den letzten
          Stand gerechnet wird — bei dreißig Zuordnungen wären das dreißig
          Durchläufe.
        */}
        {monatsfrage.fenster}
      </div>
    </Dialogfenster>
  )
}
